import { useEffect, useState } from "react";
import AzioniHomeCard from "@/components/AzioniHomeCard";
import { useNavigate, useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { nominaFiles, chiediRinomina } from "@/components/ConflittoNome";
import { Camera, MapPin, Phone, ChevronRight, Trash2, Upload, ArrowLeft, Loader2 } from "lucide-react";

function Badge({ s }) {
  const fatto = s?.stato === "fatto";
  const daFare = !s?.stato || s.stato === "richiesto";
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${fatto ? "bg-emerald-100 text-emerald-700" : daFare ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{fatto ? "✓ Fatto" : daFare ? "Da fare" : "Programmato"}</span>;
}

function Thumb({ f, onDel, onRen }) {
  const [url, setUrl] = useState(null);
  useEffect(() => { let u; api.get(`/fotografo/foto/${f.id}`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {}); return () => u && URL.revokeObjectURL(u); }, [f.id]);
  return (
    <div className="relative rounded-xl overflow-hidden bg-slate-100 aspect-square" data-testid={`foto-${f.id}`}>
      {url && (f.content_type || "").startsWith("video") ? <video src={url} controls className="w-full h-full object-cover" data-testid={`video-${f.id}`} /> : url && <img src={url} alt={f.nome} className="w-full h-full object-cover" />}
      {f.mio && <button data-testid={`foto-elimina-${f.id}`} onClick={() => onDel(f)} className="absolute top-1.5 right-1.5 p-1.5 rounded-lg bg-white/90 text-red-600"><Trash2 size={13} /></button>}
      <div className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[10px] px-1.5 py-1 truncate flex items-center gap-1">
        <span className="truncate flex-1" data-testid={`foto-nome-${f.id}`}>{f.nome}</span>
        {f.mio && <button data-testid={`foto-rinomina-${f.id}`} onClick={() => onRen(f)} className="underline shrink-0">Rinomina</button>}</div>
    </div>
  );
}

function Cartella({ c, chiave, id, onDone }) {
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const upload = async (list) => {
    const files = await nominaFiles(list);
    if (!files.length) return;
    const fd = new FormData(); fd.append("cartella", chiave); files.forEach((f) => fd.append("files", f));
    setBusy(true);
    try { const { data } = await api.post(`/fotografo/servizi/${id}/foto`, fd, { timeout: 0 }); toast.success(`${data.caricate} file caricati`); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const del = async (f) => { if (!window.confirm("Eliminare la foto?")) return; try { await api.delete(`/fotografo/foto/${f.id}`); onDone(); } catch (e) { toast.error(apiError(e)); } };
  const ren = async (f) => { const nome = await chiediRinomina(f.nome); if (!nome || nome === f.nome) return; try { await api.patch(`/fotografo/foto/${f.id}`, { nome }); onDone(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className={`card-immo p-4 ${drag ? "ring-2 ring-[#FC82ED]" : ""}`} data-testid={`cartella-${chiave}`}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-semibold text-sm">{c.nome.split("/").pop()} <span className="text-[var(--muted)] font-normal">({c.files.length})</span></div>
        <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#111111] text-white text-xs font-semibold cursor-pointer">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Carica foto/video
          <input type="file" multiple accept="image/*,.heic,.heif,video/mp4,video/quicktime,.mp4,.mov" hidden data-testid={`carica-${chiave}`} onChange={(e) => upload(e.target.files)} />
        </label>
      </div>
      <p className="text-[11px] text-[var(--muted)] mb-2">Trascina qui più file insieme (foto JPG, PNG, HEIC fino a 25 MB; video MP4, MOV fino a 500 MB).</p>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">{c.files.map((f) => <Thumb key={f.id} f={f} onDel={del} onRen={ren} />)}</div>
    </div>
  );
}

function Dettaglio({ id }) {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const load = () => api.get(`/fotografo/servizi/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  if (!d) return <div className="skeleton h-64 rounded-2xl" />;
  return (
    <div className="fade-up" data-testid="servizio-dettaglio">
      <button onClick={() => nav("/marketing")} className="inline-flex items-center gap-1 text-sm text-[var(--muted)] mb-3"><ArrowLeft size={15} /> Foto e video immobili</button>
      <div className="card-immo p-5 mb-4">
        <div className="flex items-center gap-2 flex-wrap"><h1 className="font-display text-xl font-extrabold">{d.indirizzo}</h1><Badge s={d.servizio} /></div>
        <div className="text-sm text-slate-600 mt-1">{[d.comune, d.tipologia, d.mq ? `${d.mq} mq` : null].filter(Boolean).join(" · ")}</div>
        <div className="text-sm mt-2" data-testid="servizio-agente">Agente: <b>{d.agente || "—"}</b>{d.agente_telefono && <> · <a href={`tel:${d.agente_telefono}`} className="underline"><Phone size={12} className="inline" /> {d.agente_telefono}</a></>}</div>
        <div className="text-xs text-[var(--muted)] mt-1">Servizio del {d.servizio?.data?.split("-").reverse().join("/")}</div>
        {d.servizio?.stato !== "fatto" && <button data-testid="servizio-consegnato" onClick={async () => { if (!window.confirm("Hai finito di caricare tutto? Agente e Soci riceveranno la notifica.")) return; try { await api.post(`/fotografo/servizi/${id}/consegnato`); toast.success("Stato aggiornato: Fatto"); load(); } catch (e) { toast.error(apiError(e)); } }}
          className="mt-3 px-4 py-2 rounded-xl bg-[#FC82ED] text-black text-sm font-semibold">Segna come fatto</button>}
      </div>
      <div className="grid gap-4">
        <Cartella c={d.cartelle[0]} chiave="watermark" id={id} onDone={load} />
        <Cartella c={d.cartelle[1]} chiave="senza" id={id} onDone={load} />
      </div>
    </div>
  );
}

export default function FotografoServizi() {
  const { id } = useParams();
  const nav = useNavigate();
  const [list, setList] = useState(null);
  useEffect(() => { if (!id) api.get("/fotografo/servizi").then((r) => setList(r.data)).catch((e) => toast.error(apiError(e))); }, [id]);
  if (id) return <Dettaglio id={id} />;
  return (
    <div className="fade-up" data-testid="fotografo-servizi">
      <AzioniHomeCard />
      <h1 className="font-display text-2xl font-extrabold flex items-center gap-2 mb-4"><Camera className="text-[#FC82ED]" /> I miei servizi</h1>
      {!list ? <div className="skeleton h-40 rounded-2xl" /> : list.length === 0 ? <div className="card-immo p-8 text-center text-[var(--muted)]">Nessun servizio assegnato.</div> : (
        <div className="card-immo p-0 divide-y divide-[var(--border)]">
          {list.map((s) => (
            <button key={s.id} data-testid={`servizio-${s.id}`} onClick={() => nav(`/fotografo/${s.id}`)} className="w-full text-left p-4 flex items-center gap-3 hover:bg-slate-50">
              <MapPin size={16} className="text-[#FC82ED] shrink-0" />
              <div className="flex-1 min-w-0"><div className="font-semibold text-sm truncate">{s.indirizzo}{s.comune ? `, ${s.comune}` : ""}</div>
                <div className="text-xs text-[var(--muted)]">{s.servizio?.data?.split("-").reverse().join("/")} · {s.agente || "—"}</div></div>
              <Badge s={s.servizio} /><ChevronRight size={16} className="text-[var(--muted)]" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
